import React, { useEffect, useState, useRef } from "react";
import imageCompression from "browser-image-compression";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig, getFormDataConfig } from "../../../services/apiUtils";
import CommonLayout from "../../../layouts/CommonLayout";
import {
    FaCompactDisc,
    FaPlus,
    FaUser,
    FaFileAlt,
    FaImage,
    FaCalendarAlt,
    FaTags,
    FaToggleOn,
    FaSave,
    FaArrowLeft,
    FaExclamationCircle,
    FaCheck,
    FaInfoCircle,
    FaCloudUploadAlt,
    FaMusic,
    FaList,
    FaLink,
    FaGlobe,
    FaBuilding,
    FaAward,
    FaHistory,
    FaTrash,
    FaEdit,
    FaTimes
} from "react-icons/fa";
import "./AddAlbum.css";

function csvToArray(str) {
    return (str || "").split(",").map(s => s.trim()).filter(Boolean);
}

export default function AddAlbumPage() {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState("add"); // "add" or "recent"

    // Recently added albums
    const [recentAlbums, setRecentAlbums] = useState([]);
    const [loadingRecent, setLoadingRecent] = useState(false);

    // Edit/Delete state
    const [editingAlbum, setEditingAlbum] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [editForm, setEditForm] = useState({});

    // Dropdown data
    const [artists, setArtists] = useState([]);
    const [loadingArtists, setLoadingArtists] = useState(false);
    const [imageLoading, setImageLoading] = useState(false);
    const [error, setError] = useState(null);

    // Selected IDs
    const [artist_id, setArtistId] = useState("");

    // Search state for artist dropdown
    const [artistSearchQuery, setArtistSearchQuery] = useState("");
    const [showArtistDropdown, setShowArtistDropdown] = useState(false);
    const [selectedArtistName, setSelectedArtistName] = useState("");
    const artistDropdownRef = useRef(null);

    // Core identity
    const [album_name, setAlbumName] = useState("");
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");

    // Album type
    const [albumType, setAlbumType] = useState("album");

    // Media
    const [coverImageFile, setCoverImageFile] = useState(null);

    // Release info
    const [releaseDate, setReleaseDate] = useState("");
    const [releaseYear, setReleaseYear] = useState("");

    // Tracklist
    const [tracklist, setTracklist] = useState("");

    // Arrays (CSV)
    const [featuredArtists, setFeaturedArtists] = useState("");
    const [producers, setProducers] = useState("");
    const [labels, setLabels] = useState("");
    const [keywords, setKeywords] = useState("");

    // URLs
    const [appleMusicUrl, setAppleMusicUrl] = useState("");
    const [spotifyId, setSpotifyId] = useState("");
    const [wikipediaUrl, setWikipediaUrl] = useState("");

    // Flags
    const [isFeatured, setIsFeatured] = useState(false);

    // Genres
    const [genres, setGenres] = useState([]);
    const [selectedGenre, setSelectedGenre] = useState("");

    const handleGenreChange = (e) => {
        setSelectedGenre(e.target.value);
    };

    useEffect(() => {
        const fetchGenres = async () => {
            try {
                const response = await allAPiServicesCall.getGenres({}, getAuthConfig());
                setGenres(response.data || []);
            } catch (error) {
                console.error("Error fetching genres:", error);
            }
        };
        fetchGenres();
    }, []);

    // Fetch recent albums when tab changes to "recent"
    const fetchRecentAlbums = async () => {
        try {
            setLoadingRecent(true);
            const response = await allAPiServicesCall.getAlbums(
                { limit: 20, sort: "-createdAt" },
                getAuthConfig()
            );
            setRecentAlbums(response?.data || []);
        } catch (error) {
            console.error("Error fetching recent albums:", error);
            toast.error("Failed to load recent albums");
        } finally {
            setLoadingRecent(false);
        }
    };

    useEffect(() => {
        if (activeTab === "recent") {
            fetchRecentAlbums();
        }
    }, [activeTab]);

    // Helper to extract genre name (genre can be string or object)
    const getGenreName = (genre) => {
        if (!genre) return "";
        if (typeof genre === "string") return genre;
        if (typeof genre === "object" && genre.genreName) return genre.genreName;
        return "";
    };

    // Open edit modal
    const handleEditAlbum = (album) => {
        setEditingAlbum(album);
        setEditForm({
            album_name: album.album_name || "",
            genre: getGenreName(album.genre),
            type: album.type || "album",
            releaseYear: album.releaseYear || "",
            description: album.description || "",
        });
    };

    // Save edited album
    const handleSaveEdit = async () => {
        if (!editingAlbum) return;

        try {
            setSubmitting(true);
            const response = await allAPiServicesCall.updateAlbum(
                editingAlbum._id || editingAlbum.album_id,
                editForm,
                getAuthConfig()
            );

            if (response && response.success) {
                toast.success("Album updated successfully!");
                setEditingAlbum(null);
                fetchRecentAlbums();
            } else {
                toast.error(response?.message || "Failed to update album");
            }
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message || "Error updating album");
        } finally {
            setSubmitting(false);
        }
    };

    const getAlbumDeleteId = (album) => {
        if (!album) return "";
        const rawId = album._id ?? album.id;
        const isObjectId = (value) => typeof value === "string" && /^[a-fA-F0-9]{24}$/.test(value);
        if (typeof rawId === "string" && isObjectId(rawId)) return rawId;
        if (rawId && typeof rawId === "object") {
            if (typeof rawId.$oid === "string" && isObjectId(rawId.$oid)) return rawId.$oid;
            if (typeof rawId._id === "string" && isObjectId(rawId._id)) return rawId._id;
            if (typeof rawId.toHexString === "function") {
                const hex = rawId.toHexString();
                if (isObjectId(hex)) return hex;
            }
        }
        if (typeof album.album_id === "number") return String(album.album_id);
        if (typeof album.album_id === "string" && album.album_id.trim()) return album.album_id.trim();
        return "";
    };

    // Delete album
    const handleDeleteAlbum = async () => {
        if (!deleteConfirm) return;
        const albumId = getAlbumDeleteId(deleteConfirm);
        if (!albumId) {
            toast.error("Missing album id. Please refresh and try again.");
            return;
        }

        try {
            setDeleting(true);
            const response = await allAPiServicesCall.deleteAlbum(
                albumId,
                getAuthConfig()
            );

            if (response && response.success) {
                toast.success("Album deleted successfully!");
                setDeleteConfirm(null);
                fetchRecentAlbums();
            } else {
                toast.error(response?.message || "Failed to delete album");
            }
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message || "Error deleting album");
        } finally {
            setDeleting(false);
        }
    };

    const handleAlbumImageChange = async (e) => {
        setImageLoading(true);
        let imageFile = e.target.files[0];

        if (imageFile?.size > 5 * 1024 * 1024) {
            setError("Image size should not exceed 5 MB.");
            setImageLoading(false);
            return;
        }

        const options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1024,
            useWebWorker: true,
            fileType: imageFile.type,
        };

        try {
            const compressedFile = await imageCompression(imageFile, options);
            const formData = new FormData();
            formData.append("image", compressedFile);

            const response = await allAPiServicesCall.uploadAlbumImage(formData, getFormDataConfig(), navigate);
            setCoverImageFile(response.data.path);
            setError(null);
        } catch (error) {
            console.error("Error:", error);
            setError("Failed to Upload Album Image.");
        } finally {
            setImageLoading(false);
        }
    };

    // Debounced search for artists - only search when user types at least 2 characters
    useEffect(() => {
        if (artistSearchQuery.trim().length >= 2) {
            const timer = setTimeout(async () => {
                try {
                    setLoadingArtists(true);
                    const res = await allAPiServicesCall.listArtistsSimple(
                        { limit: 20, sort: "artist_name", q: artistSearchQuery.trim() },
                        getAuthConfig()
                    );
                    const rows = Array.isArray(res?.data) ? res.data : [];
                    setArtists(rows);
                } catch (e) {
                    console.error(e);
                    toast.error("Failed to search artists");
                } finally {
                    setLoadingArtists(false);
                }
            }, 300);
            return () => clearTimeout(timer);
        } else {
            // Clear results when search is too short
            setArtists([]);
            setLoadingArtists(false);
        }
    }, [artistSearchQuery]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (artistDropdownRef.current && !artistDropdownRef.current.contains(event.target)) {
                setShowArtistDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Auto-fill release year when release date changes
    useEffect(() => {
        if (releaseDate) {
            const year = new Date(releaseDate).getFullYear().toString();
            setReleaseYear(year);
        }
    }, [releaseDate]);

    const onSubmit = async (e) => {
        e.preventDefault();

        if (!album_name) {
            return toast.error("Please enter an Album Name");
        }
        const artistIdNum = artist_id ? Number(artist_id) : null;
        if (!artistIdNum) {
            return toast.error("Please select an Artist");
        }
        if (!selectedGenre) {
            return toast.error("Please select a Genre");
        }

        setSubmitting(true);
        try {
            const payload = {
                artist_id: artistIdNum,
                album_name: album_name.trim(),
                title: title.trim() || album_name.trim(),
                description: description || undefined,
                type: albumType,
                genre: selectedGenre,
                releaseDate: releaseDate ? new Date(releaseDate).toISOString() : undefined,
                releaseYear: releaseYear || undefined,
                tracklist: csvToArray(tracklist),
                featuredArtists: csvToArray(featuredArtists),
                producers: csvToArray(producers),
                labels: csvToArray(labels),
                keywords: csvToArray(keywords),
                apple_music_url: appleMusicUrl || undefined,
                spotifyId: spotifyId || undefined,
                wikipedia_url: wikipediaUrl || undefined,
                isFeatured: isFeatured,
                coverImage: coverImageFile || undefined,
            };

            // Remove undefined values
            Object.keys(payload).forEach(key => {
                if (payload[key] === undefined) {
                    delete payload[key];
                }
            });

            const response = await allAPiServicesCall.createAlbum(payload, getAuthConfig());

            if (response && response.success) {
                toast.success("Album created successfully!");
                // Reset form to add another album
                setAlbumName("");
                setTitle("");
                setDescription("");
                setAlbumType("album");
                setCoverImageFile(null);
                setReleaseDate("");
                setReleaseYear("");
                setTracklist("");
                setFeaturedArtists("");
                setProducers("");
                setLabels("");
                setKeywords("");
                setAppleMusicUrl("");
                setSpotifyId("");
                setWikipediaUrl("");
                setIsFeatured(false);
                // Keep artist selected for convenience when adding multiple albums for same artist
                // Refresh recent albums list
                fetchRecentAlbums();
            } else {
                toast.error(response?.message || "Failed to create album");
            }
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message || "Error creating album");
        } finally {
            setSubmitting(false);
        }
    };

    const completedFields = [
        album_name, selectedArtistName, selectedGenre
    ].filter(Boolean).length;

    const albumTypes = [
        { value: "album", label: "Album" },
        { value: "ep", label: "EP" },
        { value: "single", label: "Single" },
        { value: "compilation", label: "Compilation" },
        { value: "live", label: "Live Album" },
        { value: "soundtrack", label: "Soundtrack" },
    ];

    return (
        <CommonLayout>
            <div className="add-album-page">
                {/* Back Button */}
                <button className="add-album-back-btn" onClick={() => navigate("/top-explore")}>
                    <FaArrowLeft /> Back to Albums
                </button>

                {/* Header Section */}
                <div className="add-album-header">
                    <div className="add-album-header-content">
                        <div className="add-album-header-icon">
                            <FaPlus />
                        </div>
                        <div className="add-album-header-text">
                            <h1 className="add-album-title">
                                <FaCompactDisc style={{ color: "#0031E2" }} />
                                Add New Album
                            </h1>
                            <p className="add-album-subtitle">
                                Create a new album entry with all its details
                            </p>
                        </div>
                    </div>
                    <div className="add-album-stats">
                        <div className="add-album-stat-card">
                            <FaCheck className="add-album-stat-icon" />
                            <div className="add-album-stat-info">
                                <span className="add-album-stat-value">{completedFields}/3</span>
                                <span className="add-album-stat-label">Required</span>
                            </div>
                        </div>
                        <div className="add-album-stat-card">
                            <FaTags className="add-album-stat-icon" />
                            <div className="add-album-stat-info">
                                <span className="add-album-stat-value">{genres.length}</span>
                                <span className="add-album-stat-label">Genres</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="add-album-tabs">
                    <button
                        className={`add-album-tab ${activeTab === "add" ? "active" : ""}`}
                        onClick={() => setActiveTab("add")}
                    >
                        <FaPlus /> Add Album
                    </button>
                    <button
                        className={`add-album-tab ${activeTab === "recent" ? "active" : ""}`}
                        onClick={() => setActiveTab("recent")}
                    >
                        <FaHistory /> Recently Added ({recentAlbums.length})
                    </button>
                </div>

                {/* Add Album Tab */}
                {activeTab === "add" && (
                <form onSubmit={onSubmit}>
                    <div className="add-album-form-container">
                        {/* Artist Section */}
                        <div className="add-album-section">
                            <div className="add-album-section-header">
                                <div className="add-album-section-icon">
                                    <FaUser />
                                </div>
                                <div>
                                    <h2 className="add-album-section-title">Artist</h2>
                                    <p className="add-album-section-subtitle">Select the artist for this album</p>
                                </div>
                            </div>

                            <div className="add-album-form-grid">
                                {/* Artist Search */}
                                <div className="add-album-form-group add-album-form-full" ref={artistDropdownRef}>
                                    <label className="add-album-label">
                                        Artist <span className="add-album-label-required">*</span>
                                    </label>
                                    <div className="add-album-artist-search">
                                        <div className="add-album-input-wrapper">
                                            <input
                                                type="text"
                                                className="add-album-input"
                                                placeholder="Search for an artist..."
                                                value={selectedArtistName || artistSearchQuery}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setArtistSearchQuery(value);
                                                    setSelectedArtistName("");
                                                    setArtistId("");
                                                    setShowArtistDropdown(true);
                                                }}
                                                onFocus={() => setShowArtistDropdown(true)}
                                                required
                                                autoComplete="off"
                                            />
                                            {loadingArtists && <div className="add-album-input-spinner"></div>}
                                        </div>

                                        {showArtistDropdown && artists.length > 0 && (
                                            <div className="add-album-artist-dropdown">
                                                {artists.map(artist => (
                                                    <div
                                                        key={artist.artist_id}
                                                        className="add-album-artist-option"
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            setArtistId(artist.artist_id);
                                                            setSelectedArtistName(artist.artist_name || artist.artist || `Artist ${artist.artist_id}`);
                                                            setArtistSearchQuery("");
                                                            setShowArtistDropdown(false);
                                                        }}
                                                    >
                                                        <div className="add-album-artist-option-avatar">
                                                            <FaUser />
                                                        </div>
                                                        <span className="add-album-artist-option-name">
                                                            {artist.artist_name || artist.artist || `Artist ${artist.artist_id}`}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {showArtistDropdown && artists.length === 0 && !loadingArtists && artistSearchQuery && (
                                            <div className="add-album-artist-dropdown">
                                                <div className="add-album-artist-no-results">
                                                    {artistSearchQuery.trim().length < 2
                                                        ? "Type at least 2 characters to search..."
                                                        : `No artists found for "${artistSearchQuery}"`}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Album Identity Section */}
                        <div className="add-album-section">
                            <div className="add-album-section-header">
                                <div className="add-album-section-icon">
                                    <FaCompactDisc />
                                </div>
                                <div>
                                    <h2 className="add-album-section-title">Album Identity</h2>
                                    <p className="add-album-section-subtitle">Basic information about the album</p>
                                </div>
                            </div>

                            <div className="add-album-form-grid">
                                <div className="add-album-form-group">
                                    <label className="add-album-label">
                                        Album Name <span className="add-album-label-required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="add-album-input"
                                        placeholder="Enter album name..."
                                        value={album_name}
                                        onChange={e => setAlbumName(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="add-album-form-group">
                                    <label className="add-album-label">
                                        Title <span className="add-album-label-optional">(optional - defaults to album name)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="add-album-input"
                                        placeholder="Enter title..."
                                        value={title}
                                        onChange={e => setTitle(e.target.value)}
                                    />
                                </div>

                                <div className="add-album-form-group">
                                    <label className="add-album-label">
                                        Album Type <span className="add-album-label-required">*</span>
                                    </label>
                                    <select
                                        className="add-album-select"
                                        value={albumType}
                                        onChange={(e) => setAlbumType(e.target.value)}
                                        required
                                    >
                                        {albumTypes.map((type) => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="add-album-form-group">
                                    <label className="add-album-label">
                                        Genre <span className="add-album-label-required">*</span>
                                    </label>
                                    <select
                                        className="add-album-select"
                                        value={selectedGenre}
                                        onChange={handleGenreChange}
                                        required
                                    >
                                        <option value="">Select Genre</option>
                                        {genres.map((genre) => (
                                            <option key={genre._id} value={genre.genreName}>
                                                {genre.genreName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Description Section */}
                        <div className="add-album-section">
                            <div className="add-album-section-header">
                                <div className="add-album-section-icon">
                                    <FaFileAlt />
                                </div>
                                <div>
                                    <h2 className="add-album-section-title">Description</h2>
                                    <p className="add-album-section-subtitle">Album description and details</p>
                                </div>
                            </div>

                            <div className="add-album-form-grid single-column">
                                <div className="add-album-form-group add-album-form-full">
                                    <label className="add-album-label">
                                        Description <span className="add-album-label-optional">(optional)</span>
                                    </label>
                                    <textarea
                                        className="add-album-textarea"
                                        placeholder="Enter album description..."
                                        value={description}
                                        onChange={e => setDescription(e.target.value)}
                                        rows={4}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Cover Image Section */}
                        <div className="add-album-section">
                            <div className="add-album-section-header">
                                <div className="add-album-section-icon">
                                    <FaImage />
                                </div>
                                <div>
                                    <h2 className="add-album-section-title">Cover Image</h2>
                                    <p className="add-album-section-subtitle">Album artwork and cover image</p>
                                </div>
                            </div>

                            <div className="add-album-form-grid single-column">
                                <div className="add-album-form-group add-album-form-full">
                                    <label className="add-album-label">
                                        <FaImage /> Cover Image
                                    </label>
                                    <div className={`add-album-file-upload ${coverImageFile ? 'has-file' : ''}`}>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleAlbumImageChange}
                                        />
                                        {coverImageFile ? (
                                            <div className="add-album-file-preview">
                                                <img
                                                    src={coverImageFile}
                                                    alt="Cover preview"
                                                    className="add-album-file-preview-image"
                                                />
                                                <div className="add-album-file-preview-info">
                                                    <p className="add-album-file-preview-name">Image uploaded</p>
                                                    <span className="add-album-file-preview-change">Click to change</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <FaCloudUploadAlt className="add-album-file-upload-icon" />
                                                <p className="add-album-file-upload-text">
                                                    Click or drag to upload cover image
                                                </p>
                                                <p className="add-album-file-upload-hint">
                                                    PNG, JPG up to 5MB. Will be compressed to 1MB.
                                                </p>
                                            </>
                                        )}
                                    </div>
                                    {imageLoading && (
                                        <div className="add-album-image-loading">
                                            <div className="add-album-image-loading-spinner"></div>
                                            <span className="add-album-image-loading-text">Uploading image...</span>
                                        </div>
                                    )}
                                    {error && (
                                        <div className="add-album-error">
                                            <FaExclamationCircle />
                                            {error}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Release Information Section */}
                        <div className="add-album-section">
                            <div className="add-album-section-header">
                                <div className="add-album-section-icon">
                                    <FaCalendarAlt />
                                </div>
                                <div>
                                    <h2 className="add-album-section-title">Release Information</h2>
                                    <p className="add-album-section-subtitle">Release date and year</p>
                                </div>
                            </div>

                            <div className="add-album-form-grid">
                                <div className="add-album-form-group">
                                    <label className="add-album-label">
                                        <FaCalendarAlt /> Release Date
                                    </label>
                                    <input
                                        type="date"
                                        className="add-album-input"
                                        value={releaseDate}
                                        onChange={e => setReleaseDate(e.target.value)}
                                    />
                                </div>

                                <div className="add-album-form-group">
                                    <label className="add-album-label">
                                        Release Year <span className="add-album-label-optional">(auto-filled from date)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="add-album-input"
                                        placeholder="e.g., 2024"
                                        value={releaseYear}
                                        onChange={e => setReleaseYear(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Tracklist Section */}
                        <div className="add-album-section">
                            <div className="add-album-section-header">
                                <div className="add-album-section-icon">
                                    <FaList />
                                </div>
                                <div>
                                    <h2 className="add-album-section-title">Tracklist</h2>
                                    <p className="add-album-section-subtitle">List of tracks on this album</p>
                                </div>
                            </div>

                            <div className="add-album-form-grid single-column">
                                <div className="add-album-form-group add-album-form-full">
                                    <label className="add-album-label">
                                        <FaMusic /> Track Names <span className="add-album-label-optional">(comma separated)</span>
                                    </label>
                                    <textarea
                                        className="add-album-textarea"
                                        placeholder="Enter track names separated by commas...&#10;e.g., Track 1, Track 2, Track 3"
                                        value={tracklist}
                                        onChange={e => setTracklist(e.target.value)}
                                        rows={4}
                                    />
                                    {tracklist && (
                                        <div className="add-album-tracklist-preview">
                                            <span className="add-album-tracklist-count">
                                                {csvToArray(tracklist).length} track(s)
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Credits Section */}
                        <div className="add-album-section">
                            <div className="add-album-section-header">
                                <div className="add-album-section-icon">
                                    <FaAward />
                                </div>
                                <div>
                                    <h2 className="add-album-section-title">Credits & Labels</h2>
                                    <p className="add-album-section-subtitle">Featured artists, producers, and record labels</p>
                                </div>
                            </div>

                            <div className="add-album-form-grid">
                                <div className="add-album-form-group">
                                    <label className="add-album-label">
                                        Featured Artists <span className="add-album-label-optional">(comma separated)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="add-album-input"
                                        placeholder="e.g., Artist 1, Artist 2"
                                        value={featuredArtists}
                                        onChange={e => setFeaturedArtists(e.target.value)}
                                    />
                                </div>

                                <div className="add-album-form-group">
                                    <label className="add-album-label">
                                        Producers <span className="add-album-label-optional">(comma separated)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="add-album-input"
                                        placeholder="e.g., Max Martin, Dr. Luke"
                                        value={producers}
                                        onChange={e => setProducers(e.target.value)}
                                    />
                                </div>

                                <div className="add-album-form-group">
                                    <label className="add-album-label">
                                        <FaBuilding /> Record Labels <span className="add-album-label-optional">(comma separated)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="add-album-input"
                                        placeholder="e.g., Atlantic Records, Sony Music"
                                        value={labels}
                                        onChange={e => setLabels(e.target.value)}
                                    />
                                </div>

                                <div className="add-album-form-group">
                                    <label className="add-album-label">
                                        <FaTags /> Keywords <span className="add-album-label-optional">(comma separated)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="add-album-input"
                                        placeholder="e.g., pop, summer, dance"
                                        value={keywords}
                                        onChange={e => setKeywords(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* External Links Section */}
                        <div className="add-album-section">
                            <div className="add-album-section-header">
                                <div className="add-album-section-icon">
                                    <FaLink />
                                </div>
                                <div>
                                    <h2 className="add-album-section-title">External Links</h2>
                                    <p className="add-album-section-subtitle">Links to streaming platforms and external sources</p>
                                </div>
                            </div>

                            <div className="add-album-form-grid">
                                <div className="add-album-form-group">
                                    <label className="add-album-label">
                                        Apple Music URL
                                    </label>
                                    <input
                                        type="url"
                                        className="add-album-input"
                                        placeholder="https://music.apple.com/..."
                                        value={appleMusicUrl}
                                        onChange={e => setAppleMusicUrl(e.target.value)}
                                    />
                                </div>

                                <div className="add-album-form-group">
                                    <label className="add-album-label">
                                        Spotify ID
                                    </label>
                                    <input
                                        type="text"
                                        className="add-album-input"
                                        placeholder="e.g., 1234567890abcdef"
                                        value={spotifyId}
                                        onChange={e => setSpotifyId(e.target.value)}
                                    />
                                </div>

                                <div className="add-album-form-group add-album-form-full">
                                    <label className="add-album-label">
                                        <FaGlobe /> Wikipedia URL
                                    </label>
                                    <input
                                        type="url"
                                        className="add-album-input"
                                        placeholder="https://en.wikipedia.org/wiki/..."
                                        value={wikipediaUrl}
                                        onChange={e => setWikipediaUrl(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Flags Section */}
                        <div className="add-album-section">
                            <div className="add-album-section-header">
                                <div className="add-album-section-icon">
                                    <FaToggleOn />
                                </div>
                                <div>
                                    <h2 className="add-album-section-title">Visibility & Flags</h2>
                                    <p className="add-album-section-subtitle">Special attributes for the album</p>
                                </div>
                            </div>

                            <div className="add-album-flags-grid">
                                <label className={`add-album-flag ${isFeatured ? 'active' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={isFeatured}
                                        onChange={(e) => setIsFeatured(e.target.checked)}
                                    />
                                    <div className="add-album-flag-toggle"></div>
                                    <div className="add-album-flag-content">
                                        <span className="add-album-flag-label">Featured</span>
                                        <span className="add-album-flag-description">Show in featured section on homepage</span>
                                    </div>
                                </label>
                            </div>
                        </div>

                        {/* Submit Section */}
                        <div className="add-album-submit-section">
                            <div className="add-album-submit-hint">
                                <FaInfoCircle />
                                <span>Fields marked with * are required</span>
                            </div>
                            <div className="add-album-submit-actions">
                                <button
                                    type="button"
                                    className="add-album-btn add-album-btn-secondary"
                                    onClick={() => navigate("/top-explore")}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="add-album-btn add-album-btn-primary"
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <>Saving...</>
                                    ) : (
                                        <>
                                            <FaSave /> Save Album
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
                )}

                {/* Recently Added Albums Tab */}
                {activeTab === "recent" && (
                    <div className="add-album-recent-container">
                        {loadingRecent ? (
                            <div className="add-album-recent-loading">
                                <div className="add-album-input-spinner"></div>
                                <span>Loading recent albums...</span>
                            </div>
                        ) : recentAlbums.length === 0 ? (
                            <div className="add-album-recent-empty">
                                <FaCompactDisc className="add-album-recent-empty-icon" />
                                <p>No albums added yet</p>
                                <button
                                    className="add-album-btn add-album-btn-primary"
                                    onClick={() => setActiveTab("add")}
                                >
                                    <FaPlus /> Add Your First Album
                                </button>
                            </div>
                        ) : (
                            <div className="add-album-recent-list">
                                <div className="add-album-recent-header">
                                    <span className="add-album-recent-count">
                                        Showing {recentAlbums.length} recently added albums
                                    </span>
                                    <button
                                        className="add-album-btn add-album-btn-secondary"
                                        onClick={fetchRecentAlbums}
                                    >
                                        Refresh
                                    </button>
                                </div>
                                <div className="add-album-recent-grid">
                                    {recentAlbums.map((album) => (
                                        <div key={album._id || album.album_id} className="add-album-recent-card">
                                            <div className="add-album-recent-card-image">
                                                {album.image_link || album.coverImage ? (
                                                    <img
                                                        src={album.image_link || album.coverImage}
                                                        alt={album.album_name}
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextSibling.style.display = 'flex';
                                                        }}
                                                    />
                                                ) : null}
                                                <div className="add-album-recent-card-placeholder" style={{ display: album.image_link || album.coverImage ? 'none' : 'flex' }}>
                                                    <FaCompactDisc />
                                                </div>
                                            </div>
                                            <div className="add-album-recent-card-content">
                                                <div className="add-album-recent-card-info">
                                                    <h4 className="add-album-recent-card-title">{album.album_name}</h4>
                                                    <p className="add-album-recent-card-artist">
                                                        Artist ID: {album.artist_id}
                                                    </p>
                                                    <div className="add-album-recent-card-meta">
                                                        {album.genre && (
                                                            <span className="add-album-recent-card-genre">{getGenreName(album.genre)}</span>
                                                        )}
                                                        {album.type && (
                                                            <span className="add-album-recent-card-type">{album.type}</span>
                                                        )}
                                                    </div>
                                                    {album.createdAt && (
                                                        <span className="add-album-recent-card-date">
                                                            {new Date(album.createdAt).toLocaleDateString()}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="add-album-recent-card-actions">
                                                    <button
                                                        className="add-album-card-btn add-album-card-btn-edit"
                                                        onClick={() => handleEditAlbum(album)}
                                                        title="Edit Album"
                                                    >
                                                        <FaEdit />
                                                    </button>
                                                    <button
                                                        className="add-album-card-btn add-album-card-btn-delete"
                                                        onClick={() => setDeleteConfirm(album)}
                                                        title="Delete Album"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Edit Album Modal */}
                {editingAlbum && (
                    <div className="add-album-modal-overlay" onClick={() => setEditingAlbum(null)}>
                        <div className="add-album-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="add-album-modal-header">
                                <h3><FaEdit /> Edit Album</h3>
                                <button className="add-album-modal-close" onClick={() => setEditingAlbum(null)}>
                                    <FaTimes />
                                </button>
                            </div>
                            <div className="add-album-modal-body">
                                <div className="add-album-modal-form-group">
                                    <label>Album Name</label>
                                    <input
                                        type="text"
                                        className="add-album-input"
                                        value={editForm.album_name}
                                        onChange={(e) => setEditForm({ ...editForm, album_name: e.target.value })}
                                    />
                                </div>
                                <div className="add-album-modal-form-row">
                                    <div className="add-album-modal-form-group">
                                        <label>Genre</label>
                                        <select
                                            className="add-album-select"
                                            value={editForm.genre}
                                            onChange={(e) => setEditForm({ ...editForm, genre: e.target.value })}
                                        >
                                            <option value="">Select Genre</option>
                                            {genres.map((g) => (
                                                <option key={g._id} value={g.genreName}>
                                                    {g.genreName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="add-album-modal-form-group">
                                        <label>Type</label>
                                        <select
                                            className="add-album-select"
                                            value={editForm.type}
                                            onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                                        >
                                            <option value="album">Album</option>
                                            <option value="ep">EP</option>
                                            <option value="single">Single</option>
                                            <option value="compilation">Compilation</option>
                                            <option value="live">Live Album</option>
                                            <option value="soundtrack">Soundtrack</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="add-album-modal-form-group">
                                    <label>Release Year</label>
                                    <input
                                        type="text"
                                        className="add-album-input"
                                        value={editForm.releaseYear}
                                        onChange={(e) => setEditForm({ ...editForm, releaseYear: e.target.value })}
                                        placeholder="e.g., 2024"
                                    />
                                </div>
                                <div className="add-album-modal-form-group">
                                    <label>Description</label>
                                    <textarea
                                        className="add-album-textarea"
                                        value={editForm.description}
                                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                        rows={3}
                                    />
                                </div>
                            </div>
                            <div className="add-album-modal-footer">
                                <button
                                    className="add-album-btn add-album-btn-secondary"
                                    onClick={() => setEditingAlbum(null)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="add-album-btn add-album-btn-primary"
                                    onClick={handleSaveEdit}
                                    disabled={submitting}
                                >
                                    {submitting ? "Saving..." : "Save Changes"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {deleteConfirm && (
                    <div className="add-album-modal-overlay" onClick={() => setDeleteConfirm(null)}>
                        <div className="add-album-modal add-album-modal-sm" onClick={(e) => e.stopPropagation()}>
                            <div className="add-album-modal-header add-album-modal-header-danger">
                                <h3><FaTrash /> Delete Album</h3>
                                <button className="add-album-modal-close" onClick={() => setDeleteConfirm(null)}>
                                    <FaTimes />
                                </button>
                            </div>
                            <div className="add-album-modal-body">
                                <p className="add-album-delete-message">
                                    Are you sure you want to delete <strong>"{deleteConfirm.album_name}"</strong>?
                                </p>
                                <p className="add-album-delete-warning">
                                    This action cannot be undone.
                                </p>
                            </div>
                            <div className="add-album-modal-footer">
                                <button
                                    className="add-album-btn add-album-btn-secondary"
                                    onClick={() => setDeleteConfirm(null)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="add-album-btn add-album-btn-danger"
                                    onClick={handleDeleteAlbum}
                                    disabled={deleting}
                                >
                                    {deleting ? "Deleting..." : "Delete Album"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </CommonLayout>
    );
}
